import React, { useRef, useEffect } from "react";
import { scrollTo } from "main/javascripts/utils/ScrollUtil";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { pushUrl } from "main/javascripts/utils/UrlUtil";
import { useRouter } from "next/router";
import { MainBlock } from "main/javascripts/components/block/MainBlock";
import { BasicBlock } from "main/javascripts/components/block/BasicBlock";
import { Breadcrumb } from "main/javascripts/components/breadcrumb/Breadcrumb";
import { Pager } from "main/javascripts/components/pager/Pager";
import { useAppSelector, useAppDispatch } from "main/javascripts/store";
import {
  regionsSelector,
  numPerPageSelector,
  pageSelector,
  totalNumSelector,
  cruisesSelector,
  vesselSelector,
  loadingSelector,
  salesSelector,
} from "../lpCruiseSlice";
import { fetchCruises } from "main/javascripts/features/cruise/cruiseSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  accentColor,
  backgroundColor,
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import {
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { LpCruiseList } from "./LpCruiseList";
import { CruiseCardSkeleton } from "main/javascripts/features/cruise/components/CruiseCardSkeleton";
import { RegionList } from "main/javascripts/components/lp/RegionList";
import { LpCruiseCabinList } from "./LpCruiseCabinList";
import { LpCruiseVesselAttractions } from "./LpCruiseVesselAttractions";
import { LpCruiseVesselInfo } from "./LpCruiseVesselInfo";
import { LpCruiseFaqList } from "./LpCruiseFaqList";
import { LpCruiseIntroduction } from "./LpCruiseIntroduction";
import { LpCruiseYouTubeVideo } from "./LpCruiseYouTubeVideo";
import { LpCruiseSaleList } from "./LpCruiseSaleList";
import { LpCruiseAdvancedSearchForm } from "./LpCruiseAdvancedSearchForm";
import { LpCruiseSorterForm } from "./LpCruiseSorterForm";
import { FixedConsultationButtonBlock } from "main/javascripts/components/lp/cruise/FixedConsultationButtonBlock";

export const LpVesselShowMain = (): React.ReactElement => {
  const { t } = useTranslation(["component"]);
  const appDispatch = useAppDispatch();
  const blockRef = useRef();
  const router = useRouter();

  const vessel = useAppSelector(vesselSelector);
  const cruises = useAppSelector(cruisesSelector);
  const sales = useAppSelector(salesSelector);
  const regions = useAppSelector(regionsSelector);
  const page = useAppSelector(pageSelector);
  const totalNum = useAppSelector(totalNumSelector);
  const numPerPage = useAppSelector(numPerPageSelector);
  const isLoading = useAppSelector(loadingSelector);

  const vesselBrand = vessel.vesselBrand;
  const routerPage = router.query.page;

  // ブラウザの戻る/進むボタンにも対応できるように
  // URLクエリのpageが変化したらfetchする
  // 2ページ目以降に直接アクセスした場合の初回データもここで取得する
  useEffect(() => {
    const newPage = routerPage ? Number(routerPage) : 1;
    if (newPage !== page) {
      appDispatch(
        fetchCruises({
          params: {
            vessel_code: [vessel.code],
            page: newPage,
            sort_by: [{ sort_name: "departure_date", order: "asc" }],
          },
        })
      );
    }
  }, [routerPage]);

  const pagerHandler: (page: number) => void = async (page: number) => {
    // ページネーションをクリックしたときはURLだけ変更し
    // useEffectでfetchさせる
    pushUrl({ page }, router);
    scrollTo(0, 1000);
  };

  const breadcrumbItems: any[] = [
    {
      label: t("cruise.cruise"),
      url: `/cruise`,
    },
  ];
  breadcrumbItems.push({
    label: vesselBrand.name,
    url: vesselBrand.lpUrl,
  });
  breadcrumbItems.push({
    label: vessel.name,
  });

  const renderBreadcrumb = (): JSX.Element => {
    return (
      <div css={breadcrumbBlockStyle}>
        <div css={breadcrumbInnerStyle}>
          <div css={breadcrumbStyle}>
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>
      </div>
    );
  };

  const renderCruiseSaleList = (): JSX.Element | null => {
    if (page > 1) {
      return null;
    }

    if (!sales || sales.length === 0) {
      return null;
    }

    return (
      <div css={cruiseListBlockStyle}>
        <BasicBlock styleKey="blockWide">
          <section>
            <h2 css={saleTitleStyle}>{t("lp.cruiseSaleListTitle")}</h2>
            <div css={cruiseSaleListStyle}>
              <LpCruiseSaleList saleCruises={sales} />
            </div>
          </section>
        </BasicBlock>
      </div>
    );
  };

  const renderCruiseList = (): JSX.Element | null => {
    const cruiseCount = 10;
    const hasCruise = cruises?.length > 0;

    return (
      <div css={cruiseListBlockStyle} ref={blockRef}>
        <BasicBlock styleKey="blockWide">
          <section>
            <h2 css={titleStyle}>
              {t("lp.vesselCruiseListTitle", { name: vessel.name })}
            </h2>
            <div css={cruiseListToolbarStyle}>
              <LpCruiseAdvancedSearchForm />
              <LpCruiseSorterForm />
            </div>
            <div>
              {!isLoading
                ? hasCruise && <LpCruiseList cruises={cruises} />
                : Array.from(Array(cruiseCount)).map((_, index) => {
                    return <CruiseCardSkeleton key={index} />;
                  })}
            </div>
            <div css={pagerBlockStyle}>
              <Pager
                totalNum={totalNum}
                page={page}
                numPerPage={numPerPage}
                handler={pagerHandler}
              />
            </div>
          </section>
        </BasicBlock>
      </div>
    );
  };

  const renderYouTubeVideo = (): JSX.Element | null => {
    const youtubeLink = vessel.vesselLpContent?.youtubeLink;
    if (!youtubeLink) {
      return null;
    }
    return (
      <div css={youTubeVideoBlockStyle}>
        <BasicBlock styleKey="blockWide">
          <section>
            <LpCruiseYouTubeVideo youtubeLink={youtubeLink} />
          </section>
        </BasicBlock>
      </div>
    );
  };

  const renderCabinList = (): JSX.Element | null => {
    const vesselCabins = vessel.vesselCabins || [];
    if (!vesselCabins || vesselCabins.length === 0) {
      return null;
    }

    return (
      <div css={cruiseListBlockStyle}>
        <BasicBlock styleKey="blockWide">
          <section>
            <h2 css={titleStyle}>
              {t("lp.vesselCabinListTitle", { name: vessel.name })}
            </h2>
            <div css={cabinListStyle}>
              <LpCruiseCabinList vesselCabins={vesselCabins} />
            </div>
          </section>
        </BasicBlock>
      </div>
    );
  };

  const renderAttractionList = (): JSX.Element | null => {
    const attractions = vessel.vesselAttractions || [];
    if (!attractions || attractions.length === 0) {
      return null;
    }
    return (
      <div css={attractionBlockStyle}>
        <BasicBlock styleKey="blockWide">
          <section>
            <h2 css={titleStyle}>
              {t("lp.vesselAttractionListTitle", { name: vessel.name })}
            </h2>
            <div css={attractionListStyle}>
              <LpCruiseVesselAttractions vessel={vessel} />
            </div>
          </section>
        </BasicBlock>
      </div>
    );
  };

  const renderInfo = (): JSX.Element | null => {
    return (
      <BasicBlock styleKey="blockWide">
        <section>
          <h2 css={titleStyle}>
            {t("lp.vesselInfoTitle", { name: vessel.name })}
          </h2>
          <div css={attractionListStyle}>
            <LpCruiseVesselInfo vessel={vessel} />
          </div>
        </section>
      </BasicBlock>
    );
  };

  const renderIntroduction = (): JSX.Element | null => {
    const introduction = vessel.vesselLpContent?.introduction;
    if (!introduction) {
      return null;
    }
    return (
      <div css={introductionBlockStyle}>
        <BasicBlock styleKey="blockWide">
          <section>
            <h2 css={titleStyle}>
              {t("lp.vesselIntroductionTitle", { name: vessel.name })}
            </h2>
            <div css={introductionStyle}>
              <LpCruiseIntroduction introduction={introduction} />
            </div>
          </section>
        </BasicBlock>
      </div>
    );
  };

  const renderFaqList = (): JSX.Element | null => {
    const faqs = vessel.vesselLpContent?.faqs;
    if (!faqs || faqs.length === 0) {
      return null;
    }
    return (
      <div css={faqBlockStyle}>
        <BasicBlock styleKey="blockWide">
          <section>
            <h2 css={titleStyle}>
              {t("lp.vesselFaqTitle", { name: vessel.name })}
            </h2>
            <div css={faqListStyle}>
              <LpCruiseFaqList faqs={faqs} />
            </div>
          </section>
        </BasicBlock>
      </div>
    );
  };

  const renderRegions = (): JSX.Element | null => {
    if (!regions || regions.length === 0) {
      return null;
    }
    const listItems = regions.map((region) => {
      return {
        text: region.name,
        url: `/cruise_dst/${region.id}`,
      };
    });
    return (
      <div css={textListBlockStyle}>
        <BasicBlock styleKey="blockWide">
          <section>
            <h2 css={titleStyle}>
              {t("lp.searchByVesselRegions", { name: vessel.name })}
            </h2>
            <div css={regionListStyle}>
              <RegionList listItems={listItems} />
            </div>
          </section>
        </BasicBlock>
      </div>
    );
  };

  return (
    <MainBlock>
      <div css={blockStyle}>
        <article>
          <div css={headerBlockStyle}>
            <BasicBlock styleKey="blockWide">
              <div css={headerSubtitleStyle}>{t("cruise.vessel")}</div>
              <h1 css={headerTitleStyle}>{vessel.name}</h1>
            </BasicBlock>
          </div>
          {renderCruiseSaleList()}
          {renderCruiseList()}
          {renderYouTubeVideo()}
          {renderCabinList()}
          <div css={seconddaryAreaStyle}>
            <div css={infoBlockStyle}>
              {renderAttractionList()}
              {renderInfo()}
            </div>
            {renderIntroduction()}
            {renderFaqList()}
            {renderRegions()}
            {renderBreadcrumb()}
          </div>
        </article>
      </div>
      <FixedConsultationButtonBlock />
    </MainBlock>
  );
};

const blockStyle = css`
  padding: 0 0 3rem;
`;
const headerBlockStyle = css`
  padding-top: ${space.atom2x};
  background-color: ${backgroundColor.secondaryLightColor};
`;
const headerSubtitleStyle = css`
  padding: 0 ${space.atom} ${space.atom0_5x};
  font-size: ${fontSize.body};
  font-weight: ${fontWeight.medium};
  line-height: 1.4;
  letter-spacing: ${letterSpacing.text};
  color: ${textColor.primaryDarkColor};
  opacity: 0.5;
  ${moreThanBreakpoint("desktop")} {
    text-align: center;
  }
`;
const headerTitleStyle = css`
  padding: 0 ${space.atom};
  font-size: 1.75rem;
  font-weight: ${fontWeight.bold};
  line-height: 1.4;
  letter-spacing: ${letterSpacing.text};
  ${moreThanBreakpoint("desktop")} {
    text-align: center;
    font-size: 2.5rem;
    letter-spacing: 0.025em;
  }
`;
const cruiseListBlockStyle = css`
  padding: 0 0 ${space.atom4x};
  max-width: 100%;
  box-sizing: border-box;
  background-color: ${backgroundColor.secondaryLightColor};
`;
const titleStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline1};
  color: ${accentColor.secondaryColor2};
  padding: ${space.atom};
  font-weight: ${fontWeight.medium};
  letter-spacing: ${letterSpacing.text};
  margin: ${space.atom} 0;
  position: relative;
  padding-bottom: ${space.atom2x};
  &:before {
    content: "";
    position: absolute;
    bottom: 0;
    display: block;
    height: 1px;
    width: calc(100% - ${space.atom2x});
    background: #ddd;
  }
  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    display: block;
    height: 1px;
    width: 3rem;
    background: ${accentColor.primaryColor};
  }
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.title};
    line-height: ${lineHeight.title};
  }
`;
const saleTitleStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline1};
  color: ${accentColor.primaryColor};
  padding: ${space.atom} ${space.atom} 0;
  font-weight: ${fontWeight.medium};
  letter-spacing: ${letterSpacing.text};
  margin: ${space.atom} 0 0;
  position: relative;
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.title};
    line-height: ${lineHeight.title};
  }
  ${moreThanBreakpoint("desktop")} {
    text-align: center;
  }
`;
const cruiseSaleListStyle = css`
  padding: ${space.atom} 0;
`;
const cruiseListToolbarStyle = css`
  display: flex;
  gap: ${space.atom2x};
  padding: ${space.atom2x} ${space.atom} ${space.atom};
`;
const cabinListStyle = css`
  padding: ${space.atom} 0;
`;
const youTubeVideoBlockStyle = css`
  padding: 0 ${space.atom} ${space.atom4x};
  background-color: ${backgroundColor.secondaryLightColor};
`;
const seconddaryAreaStyle = css`
  padding: ${space.atom4x} 0;
`;
const textListBlockStyle = css`
  padding-bottom: ${space.atom4x};
`;
const infoBlockStyle = css`
  display: grid;
  grid-template-columns: 100%;
  gap: ${space.atom4x};
  max-width: ${maxWidth.page1colWide};
  margin: 0 auto ${space.atom2x};
  ${moreThanBreakpoint("desktop")} {
    max-width: ${maxWidth.page2colWide};
    margin: 0 auto ${space.atom2x};
    padding: 0 ${space.atom};
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  }
`;
const attractionBlockStyle = css`
  width: 100%;
`;
const attractionListStyle = css`
  padding: ${space.atom2_5x} ${space.atom};
`;
const introductionBlockStyle = css`
  padding-bottom: ${space.atom4x};
`;
const introductionStyle = css`
  padding: ${space.atom4x} ${space.atom};
`;
const faqBlockStyle = css`
  padding-bottom: ${space.atom4x};
`;
const faqListStyle = css`
  padding: ${space.atom4x} ${space.atom};
`;
const regionListStyle = css`
  padding: ${space.atom2_5x} ${space.atom};
`;
const breadcrumbBlockStyle = css`
  padding: ${space.atom4x} 0 ${space.atom};
  max-width: 100%;
  box-sizing: border-box;
`;
const breadcrumbInnerStyle = css`
  max-width: ${maxWidth.page1colWide};
  margin: 0 auto;
  padding: 0 ${space.atom2x};
  box-sizing: border-box;
`;
const breadcrumbStyle = css`
  padding: ${space.atom2x} 0;
  border-top: 1px solid ${borderColor.secondaryLightColor};
  border-bottom: 1px solid ${borderColor.secondaryLightColor};
`;
const pagerBlockStyle = css`
  max-width: 30rem;
  margin: 2rem auto ${space.atom2x};
`;
